import { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'

const Image = ({
  src,
  alt,
  className,
  imgClassName,
  placeholder,
  fallback = '',
  lazy = true,
  rootMargin = '400px',
  ...props
}) => {
  const containerRef = useRef(null)
  const [inView, setInView] = useState(!lazy)
  // 没有 src 时直接不进入 loading 状态，避免一直转圈
  const [loading, setLoading] = useState(!!lazy && !!src)
  const [error, setError] = useState(false)
  const [imageSrc, setImageSrc] = useState(src || placeholder || fallback)

  // 基于 IntersectionObserver 的懒加载：只有图片进入视口附近时才发起请求
  useEffect(() => {
    if (!lazy || inView || !src) return
    const el = containerRef.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true)
          observer.disconnect()
        }
      },
      { rootMargin, threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [lazy, inView, src, rootMargin])

  // 进入视口后才真正加载图片
  useEffect(() => {
    if (!src || !inView) return
    let cancelled = false
    const img = new window.Image()
    img.onload = () => {
      if (cancelled) return
      setImageSrc(src)
      setLoading(false)
      setError(false)
    }
    img.onerror = () => {
      if (cancelled) return
      setError(true)
      setLoading(false)
      setImageSrc('')
    }
    img.src = src
    return () => {
      cancelled = true
    }
  }, [src, inView, fallback])

  return (
    <div ref={containerRef} className={clsx('relative overflow-hidden', className)}>
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={alt}
          loading={lazy ? 'lazy' : 'eager'}
          decoding="async"
          className={clsx(
            'transition-opacity duration-300',
            loading && 'opacity-50',
            error && 'opacity-75',
            imgClassName
          )}
          {...props}
        />
      ) : (
        <div className="flex items-center justify-center bg-gray-200 text-gray-500">
          <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  )
}

// LazyImage is an alias for Image with lazy loading enabled by default
export const LazyImage = (props) => <Image lazy={true} {...props} />

// ResponsiveImage is an alias for Image for responsive use cases
export const ResponsiveImage = (props) => <Image {...props} />

export default Image
